import { useState } from 'react'
import { motion } from 'framer-motion'
import { Crown, RefreshCw, CheckCircle, XCircle } from 'lucide-react'
import { useSubscriptionStore } from '../stores/subscriptionStore'
// import { useUserStore } from '../stores/userStore'
import SubscriptionPlans from '../components/SubscriptionPlans'
import UsageDashboard from '../components/UsageDashboard'
import { paymentService } from '../services/paymentService'
import toast from 'react-hot-toast'

const Subscription = () => {
  const { currentSubscription, isSubscriptionActive, getActivePlan, setCurrentSubscription } = useSubscriptionStore()
  // const { user } = useUserStore()
  const [showPlans, setShowPlans] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const activePlan = getActivePlan()

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    })
  }

  const getDaysRemaining = () => {
    if (!currentSubscription) return 0
    const now = new Date()
    const endDate = new Date(currentSubscription.endDate)
    const diffTime = endDate.getTime() - now.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return Math.max(0, diffDays)
  }

  const getStatusColor = () => {
    const daysRemaining = getDaysRemaining()
    if (daysRemaining > 7) return 'text-green-400'
    if (daysRemaining > 3) return 'text-yellow-400'
    return 'text-red-400'
  }

  const getStatusIcon = () => {
    const daysRemaining = getDaysRemaining()
    if (daysRemaining > 7) return <CheckCircle className="w-5 h-5 text-green-400" />
    if (daysRemaining > 3) return <RefreshCw className="w-5 h-5 text-yellow-400" />
    return <XCircle className="w-5 h-5 text-red-400" />
  }

  const handleRefreshSubscription = async () => {
    setIsLoading(true)
    try {
      const subscription = await paymentService.getSubscriptionStatus()
      if (subscription) {
        setCurrentSubscription(subscription)
        toast.success('Subscription status updated')
      } else {
        setCurrentSubscription(null)
        toast('No active subscription found', { icon: 'ℹ️' })
      }
    } catch (error) {
      console.error('Error refreshing subscription:', error)
      toast.error('Failed to refresh subscription status')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancelSubscription = async () => {
    if (!confirm('Are you sure you want to cancel your subscription?')) {
      return
    }

    setIsLoading(true)
    try {
      const success = await paymentService.cancelSubscription()
      if (success) {
        setCurrentSubscription(null)
        toast.success('Subscription cancelled successfully')
      } else {
        toast.error('Failed to cancel subscription')
      }
    } catch (error) {
      console.error('Error cancelling subscription:', error)
      toast.error('Failed to cancel subscription')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="mobile-container min-h-screen py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mobile-spacing"
      >
        {/* Header */}
        <div className="mobile-card text-center">
          <div className="w-32 h-32 sm:w-40 sm:h-40 flex items-center justify-center mx-auto mb-6">
            <img 
              src="/rraasi_transparent.png" 
              alt="Rraasi Logo" 
              className="w-full h-full object-contain"
            />
          </div>
          <h1 className="mobile-text-large text-white mb-2">Subscription Management</h1>
          <p className="text-gray-400">
            Manage your Rraasi subscription and access premium features
          </p>
        </div>

        {/* Current Subscription Status */}
        {isSubscriptionActive() && currentSubscription && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mobile-spacing"
          >
            <div className="mobile-card">
              <div className="flex items-center justify-between mb-6">
                <h2 className="mobile-text-medium text-white flex items-center">
                  <Crown className="w-6 h-6 mr-3 text-purple-400" />
                  Current Subscription
                </h2>
                <button
                  onClick={handleRefreshSubscription}
                  disabled={isLoading}
                  className="text-purple-400 hover:text-purple-300 transition-colors"
                >
                  <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Plan Details */}
                <div className="bg-gray-800/50 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-white mb-4">{activePlan?.name}</h3>
                  <p className="text-gray-400 mb-4">{activePlan?.description}</p>
                  
                  <div className="space-y-3">
                    {activePlan?.features.slice(0, 5).map((feature, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                        <span className="text-gray-300 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Status Details */}
                <div className="bg-gray-800/50 rounded-xl p-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">Status</span>
                      <div className="flex items-center gap-2">
                        {getStatusIcon()}
                        <span className="text-green-400 font-semibold">Active</span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">Started</span>
                      <span className="text-white">
                        {formatDate(currentSubscription.startDate)}
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">Expires</span>
                      <span className="text-white">
                        {formatDate(currentSubscription.endDate)}
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">Days Remaining</span>
                      <span className={`font-semibold ${getStatusColor()}`}>
                        {getDaysRemaining()} days
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">Amount Paid</span>
                      <span className="text-white">
                        ₹{currentSubscription.amount / 100}
                      </span>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mt-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-gray-400 text-sm">Subscription Progress</span>
                      <span className="text-gray-400 text-sm">
                        {Math.round((getDaysRemaining() / (activePlan?.duration || 1)) * 100)}%
                      </span>
                    </div>
                    <div className="bg-gray-700 rounded-full h-2">
                      <div 
                        className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all duration-300"
                        style={{ 
                          width: `${Math.max(0, Math.min(100, (getDaysRemaining() / (activePlan?.duration || 1)) * 100))}%` 
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-4 mt-6">
                <button
                  onClick={() => setShowPlans(true)}
                  className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white py-3 px-6 rounded-xl font-semibold hover:from-purple-600 hover:to-pink-600 transition-all"
                >
                  Renew Subscription
                </button>
                
                <button
                  onClick={handleCancelSubscription}
                  disabled={isLoading}
                  className="flex-1 bg-gray-700 text-white py-3 px-6 rounded-xl font-semibold hover:bg-gray-600 transition-colors"
                >
                  Cancel Subscription
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Usage Dashboard */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mobile-spacing"
        >
          <UsageDashboard />
        </motion.div>

        {/* No Active Subscription */}
        {!isSubscriptionActive() && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mobile-spacing"
          >
            <div className="mobile-card text-center">
              <div className="w-20 h-20 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-6">
                <Crown className="w-10 h-10 text-gray-400" />
              </div>
              <h2 className="mobile-text-medium text-white mb-4">No Active Subscription</h2>
              <p className="text-gray-400 mb-6">
                Upgrade to unlock unlimited access to all premium features and cosmic insights.
              </p>
              <button
                onClick={() => setShowPlans(true)}
                className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-3 rounded-xl font-semibold hover:from-purple-600 hover:to-pink-600 transition-all"
              >
                Choose a Plan
              </button>
            </div>
          </motion.div>
        )}

        {/* Subscription Plans Modal */}
        {showPlans && (
          <SubscriptionPlans onClose={() => setShowPlans(false)} />
        )}
      </motion.div>
    </div>
  )
}

export default Subscription 